import { createAction, createActionGroup, emptyProps, props } from '@ngrx/store';
import {
    Clinician,
    Facility,
    GSA,
    Houseware,
    Insurance,
    Job,
    MarginCalculatorModel,
    BillingRate,
    Reimbursement,
    ProfessionSpecialtyModel,
    BaseBillingRate,
    InsuranceRequest
} from '../../models';
import { HousingModel } from '../../services/margin-calculator-housing.service';
import { UserInterfaceState } from '../state/user-interface-state';
import { UserInterfaceStateSpecialtyControl } from '../state/user-interface-specialty-control.state';
import { MarginValidationLog } from '../../models/margin-validation-log.model';

export enum MarginCalculatorAction {
    LoadMargin = '[MARGIN CALCULATOR] Load Margin',
    LoadJob = '[MARGIN CALCULATOR] Load Job',
    LoadSpecialty = '[MARGIN CALCULATOR] Load Specialty',
    LoadClinician = '[MARGIN CALCULATOR] Load Clinician',
    LoadFacility = '[MARGIN CALCULATOR] Load Facility',
    LoadBillingRates = '[MARGIN CALCULATOR] Load Billing Rates',
    LoadHousingInfo = '[MARGIN CALCULATOR] Load Housing Info',
    LoadInsuranceInfo = '[MARGIN CALCULATOR] Load Insurance Info',
    LoadExtendedInsuranceInfo = '[MARGIN CALCULATOR] Load Extended Insurance Info',
    LoadReimbursements = '[MARGIN CALCULATOR] Load Reimbursements',
    LoadGSARates = '[MARGIN CALCULATOR] Load GSARates',
    LoadPremiumBillRate = '[MARGIN CALCULATOR] Load PremiumBillRate',
    LoadHousewares = '[MARGIN CALCULATOR] Load Housewares'
}

export const loadMargin = createAction(MarginCalculatorAction.LoadMargin, props<MarginCalculatorModel>());
export const loadJob = createAction(MarginCalculatorAction.LoadJob, props<Job>());
export const loadSpecialty = createAction(MarginCalculatorAction.LoadSpecialty, props<ProfessionSpecialtyModel>());
export const loadClinician = createAction(MarginCalculatorAction.LoadClinician, props<Clinician>());
export const loadFacility = createAction(MarginCalculatorAction.LoadFacility, props<Facility>());
export const loadBillingRates = createAction(
    MarginCalculatorAction.LoadBillingRates,
    props<{ billingRates: BillingRate[] }>()
);
export const loadHousingInfo = createAction(MarginCalculatorAction.LoadHousingInfo, props<HousingModel>());
export const loadInsuranceInfo = createAction(MarginCalculatorAction.LoadInsuranceInfo, props<Insurance>());
export const loadExtendedInsuranceInfo = createAction(
    MarginCalculatorAction.LoadExtendedInsuranceInfo,
    props<Insurance>()
);
export const loadReimbursements = createAction(MarginCalculatorAction.LoadReimbursements, props<Reimbursement>());
export const loadGSARates = createAction(MarginCalculatorAction.LoadGSARates, props<GSA>());
export const setPremiumBillRate = createAction(
    MarginCalculatorAction.LoadPremiumBillRate,
    props<{ premiumBillRate: number }>()
);
export const loadHousewares = createAction(MarginCalculatorAction.LoadHousewares, props<{ housewares: Houseware[] }>());

export const MarginCalculatorPageActions = createActionGroup({
    source: 'Margin Calculator Page',
    events: {
        noOp: props<{ debug?: string }>(),
        loadMargin: props<{ mcId: number }>(),
        loadJob: props<{ jobId: number; nurseId: number }>(),
        loadSpecialty: props<{ professionId: number; expertiseId: number }>(),
        loadClinician: props<{ clinicianId: number }>(),
        loadClinicianNames: props<{ name: string }>(),
        loadClinicianEmails: props<{ email: string }>(),
        clinicianSelected: props<{ clinician: Clinician }>(),
        facilitySelected: props<{ facilityId: number }>(),
        loadFacility: props<{ facilityId: number }>(),
        facilityNameControlBlur: props<{ name: string }>(),
        loadFacilityByName: props<{ name: string }>(),
        facilityNameIsSame: props<{ name: string }>(),
        facilityLoadingInProgress: emptyProps(),
        loadFacilities: props<{ name: string }>(),
        loadBillingRates: props<{ facilityId: number; professionId: number; specialtyId: number; weeks: number }>(),
        clearBillingRates: emptyProps(),
        loadMarginsDifferences: props<{ previousMarginId: number; currentMarginId: number }>(),
        setFeatureFlag: props<{ featureName: string; isEnabled: boolean }>(),
        linkToOfferDialogClosed: emptyProps(),
        setMargin: props<{ margin: MarginCalculatorModel }>(),
        setMarginId: props<{ marginId: number }>(),
        setContractId: props<{ contractId: number }>(),
        setClinician: props<{ clinician: Clinician }>(),
        setFacility: props<{ facility: Facility }>(),
        baseBillingRateChanged: props<{ rate: number }>(),
        onMarginPayRateChanged: props<{ payRate: number }>(),
        setBaseBillingRate: props<{ baseBillingRate: BaseBillingRate }>(),
        setBaseBillingRateAlt: props<{ baseBillingRateAlt: BaseBillingRate }>(),
        setBaseBillingRateAltValue: props<{ baseBillingRateAltValue: number }>(),
        candidateSelected: props<{ candidateName: string }>(),
        specialtyIdSelected: props<{ specialtyId: number }>(),
        specialtySelected: props<{ specialty: ProfessionSpecialtyModel }>(),
        setPendingOfficialOffer: props<{ pendingOfficialOffer: boolean }>(),
        setStartDate: props<{ startDate: string }>(),
        setEndDate: props<{ endDate: string }>(),
        vacationSelected: props<{ isVacation: boolean }>(),
        setInternalCosts: props<{ internalCosts: number }>(),
        setAYABooster: props<{ ayaBooster: number }>(),
        setIsExtension: props<{ isExtension: boolean }>(),
        setShiftType: props<{ altShift: number }>(),
        setIsPendingFacilityApproval: props<{ isPendingFacilityApproval: boolean }>(),
        setShiftHours: props<{ hours: number }>(),
        setAltShiftHours: props<{ hoursAlt: number }>(),
        setShifts: props<{ shifts: number }>(),
        setAltShifts: props<{ shiftsAlt: number }>(),
        setShiftTime: props<{ shiftTime: number }>(),
        setAltShiftTime: props<{ shiftTimeAlt: number }>(),
        setVacationDates: props<{ dates: Date[] }>(),
        setWeeks: props<{ weeks: number; lastWeeksLength: number }>(),
        setBookingWeeks: props<{ bookingWeeks: number }>(),
        resetWeeks: emptyProps(),
        setContractDays: props<{ contractDays: number; lastDaysLength: number }>(),
        resetContractDays: emptyProps(),
        setTimeFrame: props<{ shiftH: number; shiftM: number; shiftToH: number; shiftToM: number }>(),
        setStartTimeFrameHours: props<{ shiftH: number }>(),
        setStartTimeFrameMinutes: props<{ shiftM: number }>(),
        setEndTimeFrame: props<{ shiftToH: number; shiftToM: number }>(),
        setEndTimeFrameHours: props<{ shiftToH: number }>(),
        setEndTimeFrameMinutes: props<{ shiftToM: number }>(),
        setAltTimeFrame: props<{
            shiftAltH: number;
            shiftAltM: number;
            shiftAltToH: number;
            shiftAltToM: number;
        }>(),
        setAltStartTimeFrameHours: props<{ shiftAltH: number }>(),
        setAltStartTimeFrameMinutes: props<{ shiftAltM: number }>(),
        setAltEndTimeFrame: props<{ shiftAltToH: number; shiftAltToM: number }>(),
        setAltEndTimeFrameHours: props<{ shiftAltToH: number }>(),
        setAltEndTimeFrameMinutes: props<{ shiftAltToM: number }>(),
        setBillingOvertimeEnabled: props<{ isBillOverTime: boolean }>(),
        setFirstOvertimeHours: props<{ overtimeHours: number }>(),
        setSecondOvertimeHours: props<{ billDailyOtAfter: number }>(),
        setFirstOvertimeFrequency: props<{ billDorW: string }>(),
        setSecondOvertimeFrequency: props<{ billDorWM: string }>(),
        setAndOrBillingRule: props<{ andOr: string }>(),
        setFirstOrientationHours: props<{ orientationValue: number }>(),
        setNextOrientationHours: props<{ orientationNextValue: number }>(),
        setFirstOrientationAmount: props<{ orientationAmount: number }>(),
        setNextOrientationAmount: props<{ orientationNextAmount: number }>(),
        setFirstOrientationType: props<{ orientationType: string }>(),
        setNextOrientationType: props<{ orientationNextType: string }>(),
        setOrientationAllHoursBillable: props<{ orientationAllHoursBillable: boolean }>(),
        setLockDown: props<{ lockDown: boolean }>(),
        setRegularPayHours: props<{ regPayHrs: number }>(),
        setOvertimePayHours: props<{ otPayHrs: number }>(),
        setAltRegularPayHours: props<{ altRegPayHrs: number }>(),
        setAltOvertimePayHours: props<{ altOtPayHrs: number }>(),
        setPayHours: props<{ regPayHrs: number; otPayHrs: number; altRegPayHrs: number; altOtPayHrs: number }>(),
        setBillingRegularHours: props<{ billingRegHours: number }>(),
        setBillingOvertimeHours: props<{ billingOtHours: number }>(),
        setWeeklyHours: props<{ weeklyHours: number }>(),
        setHalfwayDate: props<{ halfwayDate: string }>(),
        setBillableHours: props<{ billingRegHours: number; billingOtHours: number }>(),
        billingRulesFlagSet: emptyProps(),
        shiftHoursAdjusted: emptyProps(),
        altShiftHoursAdjusted: emptyProps(),
        vacationFlagTurnedOff: emptyProps(),
        vacationFlagTurnedOn: emptyProps(),
        hoursAndWeeksAdjusted: emptyProps(),
        billableHoursAdjusted: emptyProps(),
        lockDownLogicAdjusted: emptyProps(),
        removeLockDownLogic: emptyProps(),
        resetPrimaryBillRateCalled: emptyProps(),
        resetAlternateBillRateCalled: emptyProps(),
        weeklyHoursAdjusted: emptyProps(),
        setAyaHousing: props<{ housing: boolean }>(),
        setPremiumMargin: props<{ premiumMargin: number }>(),
        loadInsuranceInfo: props<{ insuranceRequest: InsuranceRequest; insuranceSelectionRecentTrigger: string }>(),
        setInsuranceNeeded: props<{ isBenefit: boolean }>(),
        setInsuranceInfo: props<{ insuranceInfo: Insurance }>(),
        setMedical: props<{ medical: boolean }>(),
        setDental: props<{ dental: boolean }>(),
        setIsExtendedBenefits: props<{ isExtendedBenefits: boolean }>(),
        setRecalculateInsurance: props<{ recalculateInsurance: boolean }>(),
        setInsuranceProviderId: props<{ providerId: number }>(),
        setTotalInsuranceCostCalculated: props<{ totalInsuranceCostCalculated: number }>(),
        setTotalMedicalAndDentalCostCalculated: props<{
            totalMedicalCostCalculated: number;
            totalDentalCostCalculated: number;
        }>(),
        setMedicalDays: props<{ medicalDays: number }>(),
        setDentalDays: props<{ dentalDays: number }>(),
        setNewEffectiveDate: props<{ newEffectiveDate: Date }>(),
        setOldEffectiveDate: props<{ oldEffectiveDate: Date }>(),
        setReleaseNewEffectiveDate: props<{ releaseNewEffectiveDate: Date }>(),
        loadMarginStateInsuranceEffectiveDates: emptyProps(),
        setMarginMedicalDays: props<{ noOfMedicalDays: number }>(),
        setMarginDentalDays: props<{ noOfDentalDays: number }>(),
        SetMarginDentalFlagAndDays: props<{ marginDentalDays: number; marginDental: boolean }>(),
        setMarginMedicalFlagAndDays: props<{ marginMedicalDays: number; marginMedical: boolean }>(),
        setMarginMedical: props<{ isMedical: boolean }>(),
        setMarginDental: props<{ isDental: boolean }>(),
        setShowShiftAmount: props<{ showShiftAmt: boolean }>(),
        setOriginalBillingRate: props<{ billingRate: number }>(),
        setOriginalBillingRateAlt: props<{ billingRate: number }>(),
        setMarginReviewAndSendEnabled: props<{ reviewAndSendEnabled: boolean }>(),
        setMarginReviewAndSendForContractRequest: props<{ reviewAndSendEnabled: boolean }>(),
        revealMarginDifferencesDetails: emptyProps(),
        marginCalculatorComponentLoaded: emptyProps(),
        setTaxRate: props<{ taxRate: number }>(),
        linkToOfferClicked: emptyProps(),
        revealCalculationDetails: emptyProps(),
        revealVacationDetails: emptyProps(),
        revealPayPackageDetails: emptyProps(),
        setOverrideIneligibility: props<{ overrideIneligibility: boolean }>(),
        setRequireOverride: props<{ requireOverride: boolean }>(),
        setInsuranceOverrideOption: props<{
            overrideIneligibility: boolean;
            requireOverride: boolean;
            insuranceBenefitTypes: number;
        }>(),
        setIneligibilityDialog: props<{ overrideIneligibility: boolean; isBenefit: boolean }>(),
        setMarginDentalInfo: props<{ isDental: boolean; noOfDentalDays: number }>(),
        setMarginMedicalInfo: props<{ isMedical: boolean; noOfMedicalDays: number }>(),
        setMarginMedicalAndDentalInfo: props<{
            isMedical: boolean;
            noOfMedicalDays: number;
            isDental: boolean;
            noOfDentalDays: number;
        }>(),
        clearMarginMedicalAndDentalInfo: emptyProps(),
        showOverrideInsuranceDialog: props<{ insuranceSelectionRecentTrigger: string }>(),
        monthlyRentChanged: props<{ monthlyRent: number }>(),
        setMarginNotes: props<{ internalCostNotes: string }>(),
        loadFacilityMinimumWage: emptyProps(),
        loadMinimumWage: emptyProps(),
        setImportedFromMarginId: props<{ importedFromMarginId: number }>(),
        clearPreviousMarginIdAndMarginsDifferences: emptyProps(),
        setPremiumBillRate: props<{ premiumBillRate: number }>(),
        duplicateMargin: emptyProps(),
        contractMod: emptyProps(),
        contractModWithPreviousMarginId: props<{
            mailto: string;
            cc: string;
            subject: string;
            body: string;
            previousMarginId: number;
        }>(),
        contractModSendEmail: props<{ mailto: string; cc: string; subject: string; body: string }>(),
        contractModNotFound: emptyProps(),
        saveValidations: emptyProps(),
        loadProfessionWithSpecialties: props<{ professionId: number }>(),
        resetOrientationIndicator: emptyProps(),
        saveMarginValidationLog: props<{ marginValidationLog: MarginValidationLog }>()
    }
});

export const MarginCalculatorComputationActions = createActionGroup({
    source: 'Margin Calculator Computation',
    events: {
        billableHoursComputedSuccess: props<{
            billingRegHours: number;
            billingOtHours: number;
            primaryNonStandardOvertimeBillingHours: number;
            alternateNonStandardOvertimeBillingHours: number;
        }>(),
        lockDownComputedSuccess: props<{ lockDown: boolean }>(),
        billingOvertimeEnabledComputedSuccess: props<{ isBillOverTime: boolean }>(),
        payHoursComputedSuccess: props<{
            regPayHrs: number;
            otPayHrs: number;
            altRegPayHrs: number;
            altOtPayHrs: number;
        }>(),
        baseBillingRateComputedSuccess: props<{ baseBillingRate: BaseBillingRate }>(),
        baseBillingRateAltComputedSuccess: props<{ baseBillingRateAlt: BaseBillingRate }>(),
        resetBaseBillingRateAlt: emptyProps(),
        timeFrameComputedSuccess: props<{ shiftH: number; shiftM: number; shiftToH: number; shiftToM: number }>(),
        altTimeFrameComputedSuccess: props<{
            shiftAltH: number;
            shiftAltM: number;
            shiftAltToH: number;
            shiftAltToM: number;
        }>(),
        endTimeFrameComputedSuccess: props<{ shiftToH: number; shiftToM: number }>(),
        altEndTimeFrameComputedSuccess: props<{ shiftAltToH: number; shiftAltToM: number }>(),
        endTimeFrameHoursComputedSuccess: props<{ shiftToH: number }>(),
        altEndTimeFrameHoursComputedSuccess: props<{ shiftAltToH: number }>(),
        contractDaysComputedSuccess: props<{ contractDays: number; lastDaysLength: number }>(),
        weeklyHoursComputedSuccess: props<{ weeklyHours: number }>(),
        halfwayDateComputedSuccess: props<{ halfwayDate: string }>(),
        loadTargetMargin: props<{ facilityId: number; jobId?: number; isExtension: boolean }>(),
        loadTargetMarginWithProfession: props<{ facilityId: number; professionId: number; isExtension: boolean }>(),
        loadTargetMarginWithExpertise: props<{
            facilityId: number;
            professionId: number;
            specialtyId: number;
            isExtension: boolean;
        }>(),
        setTargetMargin: props<{ targetMargin: number }>(),
        premiumBillingRateComputedSuccess: props<{ premiumBillRate: number }>(),
        insuranceNeededComputedSuccess: props<{ isBenefit: boolean }>(),
        isExtendedBenefitsComputedSuccess: props<{ isExtendedBenefits: boolean }>()
    }
});

export const MarginCalculatorUserInterfaceActions = createActionGroup({
    source: 'Margin Calculator User Interface',
    events: {
        setUserInterfaceState: props<{ userInterfaceState: UserInterfaceState }>(),
        setUserInterfaceSpecialtyControl: props<{ specialtyControl: UserInterfaceStateSpecialtyControl }>(),
        setUserInterfaceInitialSpecialtyChanged: props<{ isInitialSpecialtyChanged: boolean; specialtyId: number }>(),
        setUserInterfaceInitialFacilityChanged: props<{ isInitialFacilityChanged: boolean }>(),
        setUserInterfaceInitialClinicianChanged: props<{ isInitialClinicianChanged: boolean }>(),
        setIsMarginCreatedFromJob: props<{ isMarginCreatedFromJob: boolean }>()
    }
});

export enum UpdateMarginActionSource {
    SaveMargin = 1,
    ReviewAndSend = 2,
    CreateContractRequest = 3,
    DuplicateMargin = 4,
    PipelineMargin = 5
}
